/**
 * MemberLocation — the single way a derived member location is displayed.
 *
 * Every admin surface that shows a location uses this component, so a
 * low-confidence guess can never look like a payment-country fact on one screen
 * and a caveat on another.
 *
 * Two rules it exists to enforce:
 *   1. A location is always shown WITH its provenance and confidence.
 *   2. `unknown` renders as "Unknown" in muted text — never a blank cell, and
 *      never a guess.
 *
 * This is the DERIVED location (network/commercial signals). It is deliberately
 * distinct from `users.location`, the free-text field a member typed about
 * themselves. Never merge the two.
 */

import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

export type LocationConfidence = 'high' | 'medium' | 'low' | 'none' | null | undefined;

export type LocationSource =
  | 'payment_stripe'
  | 'payment_apple'
  | 'payment_google'
  | 'ip_registration'
  | 'ip_session'
  | 'device_locale'
  | 'unknown'
  | null
  | undefined;

export interface MemberLocationFields {
  location_country_code?: string | null;
  location_region?: string | null;
  location_city?: string | null;
  location_source?: LocationSource;
  location_confidence?: LocationConfidence;
  location_resolved_at?: string | null;
}

interface Props extends MemberLocationFields {
  /** Compact mode drops the city line — for dense tables. */
  compact?: boolean;
}

const SOURCE_LABEL: Record<string, string> = {
  payment_stripe: 'Stripe billing country',
  payment_apple: 'Apple storefront',
  payment_google: 'Google Play region',
  ip_registration: 'Registration IP',
  ip_session: 'Most recent session IP',
  device_locale: 'Device locale',
  unknown: 'No signal available',
};

const CONFIDENCE_LABEL: Record<string, string> = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
  none: 'None',
};

/** ISO 3166-1 alpha-2 -> regional indicator pair. */
export function flagEmoji(cc?: string | null): string {
  if (!cc || cc.length !== 2 || !/^[A-Za-z]{2}$/.test(cc)) return '';
  const base = 0x1f1e6;
  return String.fromCodePoint(
    base + (cc.toUpperCase().charCodeAt(0) - 65),
    base + (cc.toUpperCase().charCodeAt(1) - 65)
  );
}

/**
 * Country name from the code, via the platform's own locale data — no hand-kept
 * country table to drift out of date.
 */
export function countryName(cc?: string | null): string {
  if (!cc) return '';
  try {
    const dn = new Intl.DisplayNames(['en'], { type: 'region' });
    return dn.of(cc.toUpperCase()) || cc.toUpperCase();
  } catch {
    return cc.toUpperCase();
  }
}

const MemberLocation: React.FC<Props> = ({
  location_country_code,
  location_region,
  location_city,
  location_source,
  location_confidence,
  location_resolved_at,
  compact = false,
}) => {
  // No signal is a normal, expected outcome. Say so plainly.
  if (!location_country_code) {
    return (
      <Tooltip title="No payment, session, or device signal has been observed for this member yet.">
        <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
          Unknown
        </Typography>
      </Tooltip>
    );
  }

  const isLow = location_confidence === 'low' || location_confidence === 'none';
  const locality = [location_city, location_region].filter(Boolean).join(', ');

  const tooltip = [
    `Source: ${SOURCE_LABEL[location_source || 'unknown'] || location_source}`,
    `Confidence: ${CONFIDENCE_LABEL[location_confidence || 'none'] || location_confidence}`,
    location_resolved_at ? `Resolved: ${new Date(location_resolved_at).toLocaleString()}` : null,
    isLow ? 'Treat as indicative only — do not act on this alone.' : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Tooltip title={tooltip}>
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
        <Typography
          variant="body2"
          component="span"
          sx={{
            // Low confidence is visually demoted so it can never be mistaken for
            // an authoritative payment country at a glance.
            color: isLow ? 'text.disabled' : 'text.primary',
            fontStyle: isLow ? 'italic' : 'normal',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          <span aria-hidden="true">{flagEmoji(location_country_code)}</span>{' '}
          {countryName(location_country_code)}
          {!compact && locality ? ` · ${locality}` : ''}
        </Typography>
        {isLow && (
          <Tooltip title="Low confidence — this is a weak signal, not a verified location.">
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.25,
                px: 0.5,
                borderRadius: 0.5,
                bgcolor: 'warning.light',
                color: 'warning.contrastText',
                fontSize: '0.65rem',
                fontWeight: 600,
                lineHeight: 1.6,
                textTransform: 'uppercase',
              }}
            >
              <HelpOutlineIcon sx={{ fontSize: '0.75rem' }} />
              low
            </Box>
          </Tooltip>
        )}
      </Box>
    </Tooltip>
  );
};

export default MemberLocation;
